import { useRef, useState } from "react";
import { Avatar } from "@/components/Avatar";
import { EmojiPicker } from "@/messaging/EmojiPicker";
import type { PresenceStatus, UserBrief } from "@/messaging/types";
import { Attachment } from "./Attachment";
import {
  AlertIcon,
  CheckCheckIcon,
  CheckIcon,
  EditIcon,
  ReplyIcon,
  RetryIcon,
  SmileIcon,
  TrashIcon,
} from "./icons";
import { clockTime } from "./time";
import type { ChatMessage } from "./useMessages";

const QUICK = ["👍", "❤️", "😂", "🎉", "🙏", "🔥"];

export function MessageRow({
  message,
  mine,
  showAvatar,
  showName,
  senderStatus,
  onOpenImage,
  onReply,
  onEdit,
  onDelete,
  onReact,
  onRetry,
  seen,
  seenBy,
  animate,
}: {
  message: ChatMessage;
  mine: boolean;
  showAvatar: boolean;
  showName: boolean;
  senderStatus?: PresenceStatus;
  onOpenImage: (url: string) => void;
  onReply: (m: ChatMessage) => void;
  onEdit: (id: string, body: string) => void;
  onDelete: (id: string) => void;
  onReact: (id: string, emoji: string) => void;
  onRetry: (localId: string) => void;
  seen?: boolean;
  /** Members who've read up to this message (own last message only). */
  seenBy?: UserBrief[];
  /** Play the bubble-in animation (newest message only). */
  animate?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.body ?? "");
  const [pickerOpen, setPickerOpen] = useState(false);
  const reactBtnRef = useRef<HTMLButtonElement | null>(null);

  const deleted = !!message.deleted_at;
  const name =
    message.sender?.full_name || message.sender?.email || "Unknown";

  const bubbleBase =
    "relative px-3.5 py-2 rounded-2xl text-sm leading-relaxed break-words whitespace-pre-wrap max-w-full";
  const bubbleTone = deleted
    ? "bg-white/[0.03] border border-white/5 text-slate-500 italic"
    : mine
      ? "bg-accent-600 text-white"
      : "bg-ink-800 border border-white/5 text-slate-100";

  const commitEdit = () => {
    const t = draft.trim();
    if (t && t !== message.body) onEdit(message.id, t);
    setEditing(false);
  };

  return (
    <div
      className={`group/msg flex gap-2 px-3 ${mine ? "flex-row-reverse" : "flex-row"} ${
        showAvatar ? "mt-2" : "mt-0.5"
      } ${animate ? "animate-[bubbleIn_.2s_ease-out]" : ""}`}
    >
      {/* avatar gutter (others only) */}
      {!mine && (
        <div className="w-7 flex-shrink-0 self-end">
          {showAvatar && (
            <Avatar
              url={message.sender?.avatar_url}
              name={name}
              email={message.sender?.email}
              size="xs"
              status={senderStatus}
            />
          )}
        </div>
      )}

      <div className={`flex flex-col min-w-0 max-w-[78%] ${mine ? "items-end" : "items-start"}`}>
        {showName && !mine && (
          <span className="text-[11px] font-medium text-slate-400 px-1 mb-0.5">
            {name}
          </span>
        )}

        {/* reply quote */}
        {message.reply_to && !deleted && (
          <div
            className={`mb-1 max-w-full rounded-lg border-l-2 pl-2 pr-2.5 py-1 text-[12px] ${
              mine
                ? "border-white/40 bg-white/10 text-white/80"
                : "border-accent-500/60 bg-white/[0.03] text-slate-400"
            }`}
          >
            <div className="font-medium truncate text-[11px] opacity-90">
              {message.reply_to.sender_name || "Unknown"}
            </div>
            <div className="truncate">
              {message.reply_to.deleted
                ? "Deleted message"
                : message.reply_to.body || (message.reply_to.has_attachment ? "Attachment" : "")}
            </div>
          </div>
        )}

        <div className={`flex items-end gap-1.5 ${mine ? "flex-row-reverse" : ""}`}>
          {/* the bubble */}
          {editing ? (
            <div className="rounded-2xl bg-ink-850 border border-white/10 p-1.5 w-72 max-w-full">
              <textarea
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    commitEdit();
                  }
                  if (e.key === "Escape") setEditing(false);
                }}
                className="input w-full resize-none max-h-40 min-h-[2.25rem] !py-1.5 !text-sm"
                rows={2}
              />
              <div className="flex justify-end gap-1.5 mt-1">
                <button
                  className="text-[11px] px-2 py-1 rounded text-slate-400 hover:text-slate-200"
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </button>
                <button
                  className="text-[11px] px-2.5 py-1 rounded bg-accent-600 hover:bg-accent-500 text-white font-medium"
                  onClick={commitEdit}
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div
              className={`${bubbleBase} ${bubbleTone} ${
                message.pending ? "opacity-60" : ""
              } ${message.failed ? "!border-rose-500/50 !bg-rose-500/10" : ""}`}
            >
              {deleted ? (
                "Message deleted"
              ) : (
                <>
                  {message.body && <span>{message.body}</span>}
                  {message.attachment_url && (
                    <Attachment
                      url={message.attachment_url}
                      kind={message.attachment_kind}
                      onOpenImage={onOpenImage}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* hover toolbar */}
          {!deleted && !editing && !message.pending && (
            <div className="flex items-center gap-0.5 opacity-0 group-hover/msg:opacity-100 transition-opacity self-center">
              <button
                ref={reactBtnRef}
                onClick={() => setPickerOpen((v) => !v)}
                className="icon-btn h-7 w-7"
                title="React"
              >
                <SmileIcon size={15} />
              </button>
              <button
                onClick={() => onReply(message)}
                className="icon-btn h-7 w-7"
                title="Reply"
              >
                <ReplyIcon size={15} />
              </button>
              {mine && (
                <>
                  {message.body != null && (
                    <button
                      onClick={() => {
                        setDraft(message.body ?? "");
                        setEditing(true);
                      }}
                      className="icon-btn h-7 w-7"
                      title="Edit"
                    >
                      <EditIcon size={15} />
                    </button>
                  )}
                  <button
                    onClick={() => onDelete(message.id)}
                    className="icon-btn h-7 w-7 hover:!text-rose-300"
                    title="Delete"
                  >
                    <TrashIcon size={15} />
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* quick-react bar (compact) when picker open */}
        {pickerOpen && (
          <>
            <div className="mt-1 flex items-center gap-1 rounded-full bg-ink-850 border border-white/10 px-1.5 py-1 shadow-lg">
              {QUICK.map((e) => (
                <button
                  key={e}
                  onClick={() => {
                    onReact(message.id, e);
                    setPickerOpen(false);
                  }}
                  className="h-7 w-7 rounded-full hover:bg-white/10 text-base leading-none"
                >
                  {e}
                </button>
              ))}
            </div>
            <EmojiPicker
              open={pickerOpen}
              triggerRef={reactBtnRef}
              onClose={() => setPickerOpen(false)}
              onPick={(emoji) => {
                onReact(message.id, emoji);
                setPickerOpen(false);
              }}
            />
          </>
        )}

        {/* reactions */}
        {message.reactions && message.reactions.length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-1 ${mine ? "justify-end" : ""}`}>
            {message.reactions.map((r) => (
              <button
                key={r.emoji}
                onClick={() => onReact(message.id, r.emoji)}
                className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] transition ${
                  r.me
                    ? "border-accent-500/50 bg-accent-500/15 text-accent-200"
                    : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/20"
                }`}
              >
                <span className="text-[13px] leading-none">{r.emoji}</span>
                <span className="tabular-nums">{r.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* meta line */}
        <div
          className={`flex items-center gap-1 mt-0.5 px-1 text-[10.5px] text-slate-500 ${
            mine ? "flex-row-reverse" : ""
          }`}
        >
          {message.failed ? (
            <button
              onClick={() => message.localId && onRetry(message.localId)}
              className="inline-flex items-center gap-1 text-rose-300 hover:text-rose-200"
              title="Retry"
            >
              <AlertIcon size={12} /> Failed — retry <RetryIcon size={12} />
            </button>
          ) : message.pending ? (
            <span>Sending…</span>
          ) : (
            <>
              <span>{clockTime(message.created_at)}</span>
              {message.edited_at && <span>· edited</span>}
              {mine &&
                (!seenBy || seenBy.length === 0) &&
                (seen ? (
                  <CheckCheckIcon size={13} className="text-accent-300" />
                ) : (
                  <CheckIcon size={13} className="text-slate-500" />
                ))}
            </>
          )}
        </div>

        {/* seen-by avatars (own last message) */}
        {mine && seenBy && seenBy.length > 0 && (
          <div className="mt-0.5 flex items-center justify-end gap-1 px-1">
            <div className="flex -space-x-1.5">
              {seenBy.slice(0, 4).map((u) => (
                <Avatar
                  key={u.id}
                  url={u.avatar_url}
                  name={u.full_name}
                  email={u.email}
                  size="xs"
                  className="!h-4 !w-4 ring-1 ring-ink-950"
                />
              ))}
            </div>
            {seenBy.length > 4 && (
              <span className="text-[10px] text-slate-500">+{seenBy.length - 4}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
